// apps/client/src/pages/OfficeDashboardPage.tsx
// OFFICE-SPECIFIC DASHBOARD - Detailed view with templates, brokers, etc.

import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection, filterEntityBooksForOffice } from '../utils/loanBookResolution';
import StatCard from '../components/common/StatCard';
import StatCardWithGrowth from '../components/common/StatCardWithGrowth';
import MonthlyBreakdownChart from '../components/charts/MonthlyBreakdownChart';
import BrokerConcentrationWidget from '../components/widgets/BrokerConcentrationWidget';
import ConcentrationAlert from '../components/widgets/ConcentrationAlert';
import WhoToCallWidget from '../components/widgets/WhoToCallWidget';
import BrokerRevenueTable from '../components/tables/BrokerRevenueTable';
import TopLendersWidget from '../components/widgets/TopLendersWidget';
import { HandCoins, TrendingUp, Vault, Activity, Building2, Handshake } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import DashboardFilters from '../components/common/DashboardFilters';
import EnhancedDashboardFilters from '../components/common/EnhancedDashboardFilters';
import ErrorBoundary from '../components/common/ErrorBoundary';
import { SkeletonDashboard, SkeletonStatCards, SkeletonCharts } from '../components/common/SkeletonLoader';
import { formatCurrencyShorthand, formatLoanBook } from '../utils/formatters';
import { useHideCommissions, useHideLoanVolume } from '../hooks/useHideCommissions';
import { adminPageDenied } from '../config/adminAccess';
import { filterAggregates } from '../utils/dashboardFilters';
import { useMonthlyAggregatesOffice } from '../hooks/office/useMonthlyAggregatesOffice';
import { useLineOfTrailKpiOffice } from '../hooks/office/useLineOfTrailKpiOffice';
import { useMonthlySettlementsOffice } from '../hooks/office/useMonthlySettlementsOffice';

import { useLenders } from '../hooks/useLenders';
import { useRolling3RunWindowOffice } from '../hooks/office/useRolling3RunWindowOffice';
import { useBrokerStatuses } from '../hooks/useBrokerStatuses';
import { useAggregationStatus } from '../hooks/useAggregationStatus';
import { useYoYComparison } from '../hooks/useYoYComparison';
import { useCompanyDataYearRange } from '../hooks/useCompanyDataYearRange';
import { calculateBrokerPercentagesAcrossMonths, getConcentrationRisk, filterAggregatesByBrokerStatus } from '../utils/brokerConcentration';
import { featureFlags } from '../config/featureFlags';
import type { EnhancedDashboardFilters as FilterType, Office, UserData, CompanyData } from '../types';
import { COMMISSION_GST_BASIS } from '../utils/commissionDisplay';

interface FileTemplate {
    id: string;
    name: string;
}

// ✅ PHASE 1B: Strongly typed context interface
interface DashboardContext {
    userData: UserData;
    companyData: CompanyData;
    userVisibleOffices: Office[];
    userVisibleTemplates: any[];
    loanBooks: ResolvedLoanBook[];
}

const OfficeDashboardPage: React.FC = () => {
    const { officeId } = useParams<{ officeId: string }>();
    const context = useOutletContext<DashboardContext>();

    // Extract context data (handle undefined case below, after all hooks)
    const { userData, companyData, userVisibleOffices, userVisibleTemplates, loanBooks = [] } = context || {};
    // `hideCommissionsClaim` is the per-user hidden-commission switch. An
    // administrator denied the dashboard commission figures gets the same
    // commission-section rendering on top (the server already zeroes the data);
    // per-loan trail (Who To Call) follows the claim only.
    const hideCommissionsClaim = useHideCommissions();
    const hideCommissions = hideCommissionsClaim || adminPageDenied(userData, 'dashboard-commissions');
    const hideLoanVolume = useHideLoanVolume();

    const office = useMemo(() =>
        userVisibleOffices?.find((o: Office) => o.id === officeId),
        [officeId, userVisibleOffices]
    );

    // State
    const [fileTemplates, setFileTemplates] = useState<FileTemplate[]>([]);
    const [brokers, setBrokers] = useState<string[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<'all' | string>('all');

    // Resolve loan book selection → query params (templateId + optional entityCode)
    const resolvedParams = useMemo(() =>
        resolveLoanBookSelection(selectedTemplate, loanBooks),
        [selectedTemplate, loanBooks]
    );

    const [filters, setFilters] = useState<FilterType>({
        broker: 'all',
        year: new Date().getFullYear(),
        yearType: 'calendar',
        activityStatus: 'all',
    });

    // Broker breakdown filter — controls BrokerRevenueTable + BrokerConcentrationWidget only (not stat cards/charts)
    const [brokerBreakdownStatus, setBrokerBreakdownStatus] = useState<'all' | 'active' | 'inactive'>('active');

    // ✅ BROKER ACTIVITY STATUS: Fetch from Firestore brokers collection
    const { brokerStatuses: brokerActivityStatus } = useBrokerStatuses(
        userData?.companyId || '',
        office?.id
    );

    // ✅ DYNAMIC YEAR RANGE: Fetch from BigQuery for year dropdown population
    const { earliestYear, latestYear } = useCompanyDataYearRange({
        companyId: userData?.companyId,
    });

    // Templates from centralized context (no per-page Firestore query — respects broker RBAC)
    useEffect(() => {
        const templates = (userVisibleTemplates || []).map((t: any) => ({
            id: t.id,
            name: t.templateName || t.name || t.id,
        }));
        setFileTemplates(templates.sort((a: FileTemplate, b: FileTemplate) => a.name.localeCompare(b.name)));
    }, [userVisibleTemplates]);

    // Fetch commission data using hooks - CURRENT YEAR
    // Note: templateId filtering happens client-side via filteredAggregates useMemo
    // ✅ FIX: Now also returns templateIds for tab visibility
    const {
        aggregates,
        loading: loadingAggregates,
        templateIds,  // ✅ FIX: Template IDs with data for this office (from BigQuery)
        upfrontCommission,
        trailCommission,
    } = useMonthlyAggregatesOffice({
        companyId: userData?.companyId || '',
        year: filters.year,
        yearType: filters.yearType,
        officeId: office?.id,
        templateId: resolvedParams.templateId,  // Use resolved templateId (not loan book doc ID)
        entityCode: resolvedParams.entityCode,
    });

    // Fetch PREVIOUS YEAR data for YoY comparison
    const {
        aggregates: previousAggregates,
        loading: loadingPreviousAggregates,
        upfrontCommission: previousUpfrontCommission,
        trailCommission: previousTrailCommission,
    } = useMonthlyAggregatesOffice({
        companyId: userData?.companyId || '',
        year: filters.year - 1,
        yearType: filters.yearType,
        officeId: office?.id,
        templateId: resolvedParams.templateId,
        entityCode: resolvedParams.entityCode,
    });

    // Reset selectedTemplate when office changes
    // Prevents stale template from previous office causing $0 data
    useEffect(() => {
        setSelectedTemplate('all');
    }, [office?.id]);

    // Fetch historical line of trail
    // ✅ FIX: Include broker/lender breakdown when filters are active
    const needsLotByBroker = filters.activityStatus !== 'all' || filters.broker !== 'all';
    const needsLotByLender = filters.lender !== undefined && filters.lender !== 'all';
    const {
        series: lineOfTrailSeries,
        loading: loadingHistorical,
    } = useLineOfTrailKpiOffice({
        companyId: userData?.companyId || '',
        officeId: office?.id,
        templateId: resolvedParams.templateId,  // Use resolved templateId
        entityCode: resolvedParams.entityCode,
        year: filters.year,
        yearType: filters.yearType,
        includeByBroker: needsLotByBroker,  // ✅ FIX: Request broker breakdown for filtering
        includeByLender: needsLotByLender,  // ✅ FIX: Request lender breakdown for filtering
    });


    // ✅ NEW: Fetch settlement data for "Total Loans Settled" chart (match Main Dashboard)
    const {
        settlements,
        loading: loadingSettlements,
        uniqueTotalSettled,
        uniqueTotalCount,
    } = useMonthlySettlementsOffice({
        companyId: userData?.companyId || '',
        year: filters.year,
        yearType: filters.yearType,
        officeId: office?.id, // ✅ Office-specific filtering
        templateId: resolvedParams.templateId, // Use resolved templateId
        entityCode: resolvedParams.entityCode,
    });

    // ✅ PHASE 3: Extract lenders from BOTH settlement and commission aggregate data
    const lenders = useLenders(settlements, aggregates);

    // ✅ NEW: Monitor aggregation status for stale-while-revalidate UX
    const isAggregating = useAggregationStatus(userData?.companyId || '');

    // ✅ PHASE 3: Apply enhanced filters to aggregates (Performance bands removed)
    // ✅ BROKER FILTERING: Extract broker-specific data from byBroker breakdown
    // ✅ ACTIVITY STATUS FILTERING: Filter aggregates by broker activity status
    const filteredAggregates = useMemo(() => {
        let baseFiltered = aggregates;

        // Apply enhanced filters first (if enabled)
        if (featureFlags.enhancedFilters) {
            baseFiltered = filterAggregates(aggregates, filters);
        }

        // ✅ Apply activity status filter (only show data from active/inactive brokers based on Firestore status)
        if (filters.activityStatus !== 'all') {
            baseFiltered = baseFiltered.map(agg => {
                if (!agg.byBroker) return agg;

                // Filter byBroker breakdown to only include brokers matching activity status from Firestore
                let totalUpfront = 0;
                let totalTrail = 0;
                let totalLoanAmount = 0;
                let totalLineOfTrail = 0;
                let totalRecords = 0;

                Object.entries(agg.byBroker).forEach(([brokerId, data]) => {
                    const brokerDisplay = data.brokerDisplay || brokerId;
                    const status = brokerActivityStatus.get(brokerDisplay) || 'active';

                    // Only include brokers matching the selected activity status
                    if (status === filters.activityStatus) {
                        totalUpfront += data.upfrontNet || 0;
                        totalTrail += data.trailNet || 0;
                        totalLoanAmount += data.totalLoanAmount || 0;
                        totalLineOfTrail += data.lineOfTrail || 0;
                        totalRecords += data.recordCount || 0;
                    }
                });

                return {
                    ...agg,
                    upfrontCommission: totalUpfront,
                    trailCommission: totalTrail,
                    totalLoanAmount,
                    lineOfTrail: totalLineOfTrail,
                    recordCount: totalRecords,
                };
            });
        }

        // ✅ Apply lender filtering if lender is selected
        // FIX (2025-12-12): Removed incorrect byTemplate lookup.
        // When templateId is passed to useMonthlyAggregates, the backend filters at SQL level,
        // so byLender at top level is already scoped to the selected template.
        if (filters.lender && filters.lender !== 'all') {
            // DEBUG: Log lender filtering state
            console.log('[OfficeDashboard] Lender filter active:', {
                selectedLender: filters.lender,
                monthsWithByLender: baseFiltered.filter(a => a.byLender).length,
                totalMonths: baseFiltered.length,
                sampleByLender: baseFiltered[0]?.byLender ? Object.keys(baseFiltered[0].byLender) : 'none',
            });

            baseFiltered = baseFiltered.map(agg => {
                if (!agg.byLender) {
                    console.log(`[OfficeDashboard] Month ${agg.yearMonth}: No byLender data`);
                    return { ...agg, upfrontCommission: 0, trailCommission: 0, totalLoanAmount: 0, lineOfTrail: 0, recordCount: 0 };
                }

                const lenderData = agg.byLender[filters.lender!];
                if (lenderData) {
                    console.log(`[OfficeDashboard] Month ${agg.yearMonth}: Found lender data for ${filters.lender}:`, {
                        upfrontNet: lenderData.upfrontNet,
                        trailNet: lenderData.trailNet,
                    });
                    return {
                        ...agg,
                        upfrontCommission: lenderData.upfrontNet || 0,
                        trailCommission: lenderData.trailNet || 0,
                        totalLoanAmount: lenderData.totalLoanAmount || 0,
                        lineOfTrail: lenderData.lineOfTrail || 0,
                        recordCount: lenderData.recordCount || 0,
                    };
                }

                console.log(`[OfficeDashboard] Month ${agg.yearMonth}: No data for lender ${filters.lender}, available:`, Object.keys(agg.byLender));
                // No data for this lender in this month
                return {
                    ...agg,
                    upfrontCommission: 0,
                    trailCommission: 0,
                    totalLoanAmount: 0,
                    lineOfTrail: 0,
                    recordCount: 0,
                };
            });
        }

        // Filter by individual broker if selected
        if (filters.broker !== 'all') {
            return baseFiltered.map(agg => {
                // Extract broker-specific data from byBroker breakdown
                const brokerData = agg.byBroker?.[Object.keys(agg.byBroker).find(
                    key => agg.byBroker![key].brokerDisplay === filters.broker
                ) || ''];

                if (brokerData) {
                    return {
                        ...agg,
                        upfrontCommission: brokerData.upfrontNet || 0,
                        trailCommission: brokerData.trailNet || 0,
                        totalLoanAmount: brokerData.totalLoanAmount || 0,
                        lineOfTrail: brokerData.lineOfTrail || 0,
                        recordCount: brokerData.recordCount || 0,
                    };
                }

                // If broker has no data for this month, return zeros
                return {
                    ...agg,
                    upfrontCommission: 0,
                    trailCommission: 0,
                    totalLoanAmount: 0,
                    lineOfTrail: 0,
                    recordCount: 0,
                };
            });
        }

        return baseFiltered;
    }, [aggregates, filters, brokerActivityStatus]);

    // ✅ OFFICE DASHBOARD: Use year-based loan book/line of trail - CURRENT YEAR
    // Respects calendar/financial year filter for consistent filtering experience
    // ✅ OPTIMIZATION: Pass broker/lender filters to backend for SQL-level filtering
    // ✅ FIX: Include broker breakdown when activity status filter is active
    const rollingWindow = useRolling3RunWindowOffice({
        companyId: userData?.companyId,
        officeId: office?.id,
        templateId: resolvedParams.templateId,
        entityCode: resolvedParams.entityCode,
        brokerId: filters.broker !== 'all' ? filters.broker : undefined,
        lender: filters.lender !== 'all' ? filters.lender : undefined,
        yearMode: {
            year: filters.year,
            yearType: filters.yearType,
        },
        includeByBroker: filters.activityStatus !== 'all',  // ✅ FIX: Request broker breakdown for activity filtering
    });

    // ✅ PREVIOUS YEAR loan book/line of trail for YoY comparison
    const previousRollingWindow = useRolling3RunWindowOffice({
        companyId: userData?.companyId,
        officeId: office?.id,
        templateId: resolvedParams.templateId,
        entityCode: resolvedParams.entityCode,
        brokerId: filters.broker !== 'all' ? filters.broker : undefined,
        lender: filters.lender !== 'all' ? filters.lender : undefined,
        yearMode: {
            year: filters.year - 1,
            yearType: filters.yearType,
        },
        includeByBroker: filters.activityStatus !== 'all',
    });

    // ✅ OPTIMIZATION: Backend now handles broker/lender filtering at SQL level
    // ✅ ACTIVITY STATUS FILTERING: Still client-side (requires Firestore brokers collection)
    const { totalLoanBook, lineOfTrail, loadingLoanBook, previousLoanBook, previousLineOfTrail } = useMemo(() => {
        // CURRENT YEAR calculation
        let currentLoanBook = 0;
        let currentLineOfTrail = 0;

        // If filtering by activity status (active/inactive) - client-side filtering required
        if (filters.activityStatus !== 'all') {
            rollingWindow.runs.forEach((run) => {
                if (run.byBroker) {
                    Object.entries(run.byBroker).forEach(([brokerId, brokerData]: [string, any]) => {
                        const brokerDisplay = brokerData.brokerDisplay || brokerId;
                        const status = brokerActivityStatus.get(brokerDisplay) || 'active';

                        if (status === filters.activityStatus) {
                            currentLoanBook += brokerData.totalLoanAmount || 0;
                            currentLineOfTrail += brokerData.lineOfTrail || 0;
                        }
                    });
                }
            });
        } else {
            // No filtering - use backend-filtered rolling window values directly
            currentLoanBook = rollingWindow.totalLoanBook;
            currentLineOfTrail = rollingWindow.totalLineOfTrail;
        }

        // PREVIOUS YEAR calculation (same logic)
        let prevLoanBook = 0;
        let prevLineOfTrail = 0;

        if (filters.activityStatus !== 'all') {
            previousRollingWindow.runs.forEach((run) => {
                if (run.byBroker) {
                    Object.entries(run.byBroker).forEach(([brokerId, brokerData]: [string, any]) => {
                        const brokerDisplay = brokerData.brokerDisplay || brokerId;
                        const status = brokerActivityStatus.get(brokerDisplay) || 'active';

                        if (status === filters.activityStatus) {
                            prevLoanBook += brokerData.totalLoanAmount || 0;
                            prevLineOfTrail += brokerData.lineOfTrail || 0;
                        }
                    });
                }
            });
        } else {
            prevLoanBook = previousRollingWindow.totalLoanBook;
            prevLineOfTrail = previousRollingWindow.totalLineOfTrail;
        }

        return {
            totalLoanBook: currentLoanBook,
            lineOfTrail: currentLineOfTrail,
            loadingLoanBook: rollingWindow.loading || previousRollingWindow.loading,
            previousLoanBook: prevLoanBook,
            previousLineOfTrail: prevLineOfTrail,
        };
    }, [rollingWindow, previousRollingWindow, filters.activityStatus, brokerActivityStatus]);

    // ✅ Extract unique brokers from aggregates
    useEffect(() => {
        if (!aggregates || aggregates.length === 0) {
            setBrokers([]);
            return;
        }

        const brokerSet = new Set<string>();

        // Extract unique brokers from commission data
        aggregates.forEach(agg => {
            if (agg.byBroker) {
                Object.entries(agg.byBroker).forEach(([brokerId, data]) => {
                    const brokerDisplay = data.brokerDisplay || brokerId;
                    if (brokerDisplay) {
                        brokerSet.add(brokerDisplay);
                    }
                });
            }
        });

        setBrokers(Array.from(brokerSet).sort());
    }, [aggregates]);

    // ✅ BROKER FILTERING: Filter broker dropdown based on Firestore status
    const filteredBrokers = useMemo(() => {
        if (filters.activityStatus === 'all') {
            return brokers;
        }

        return brokers.filter(broker => {
            const status = brokerActivityStatus.get(broker);
            // Default to 'active' if status not found in Firestore
            return (status || 'active') === filters.activityStatus;
        });
    }, [brokers, brokerActivityStatus, filters.activityStatus]);

    // ✅ AUTO-RESET: Reset broker filter to 'all' if selected broker is filtered out
    useEffect(() => {
        if (filters.broker !== 'all' && !filteredBrokers.includes(filters.broker)) {
            setFilters(prev => ({ ...prev, broker: 'all' }));
        }
    }, [filteredBrokers, filters.broker, setFilters]);

    // ✅ FIX: Filter templates to only those with data for this office
    // templateIds comes from BigQuery via useMonthlyAggregates hook
    // Show tabs ONLY if 2+ templates have data
    const filteredFileTemplates = useMemo(() => {
        if (templateIds.length === 0) return fileTemplates;  // Fallback during loading
        return fileTemplates.filter(t => templateIds.includes(t.id));
    }, [fileTemplates, templateIds]);

    // Loan book tabs: for unified mode (entity-type), GROUP BY entityCode so multiple
    // loan_books that share an entity (e.g. VFA's Choice + LMG both → entityCode
    // 'LOAN_MARKET' for the Choice → LMG rebrand) render as ONE tab. The dashboard
    // filter then queries by entityCode alone, naturally combining the timelines.
    //
    // Tab id encoding:
    //   entity-grouped tab → 'entity:<entityCode>'  (e.g. 'entity:LOAN_MARKET')
    //   single loan_book   → '<loanBookId>'         (legacy + Timms one-loan-book-per-entity)
    //
    // resolveLoanBookSelection() (apps/client/src/utils/loanBookResolution.ts) recognises
    // the 'entity:' prefix and returns { entityCode } without narrowing to a single
    // templateId, so backend queries (which respect entity_code via resolveEntityScope)
    // correctly union all of the entity's underlying templates.
    //
    // For legacy mode (template-type), filter by templates with data in THIS office.
    const loanBookTabs = useMemo(() => {
        if (loanBooks.length > 0) {
            // Entity-type loan books: group by entityCode so multi-template entities
            // (Choice + LMG → "Loan Market") collapse to one tab.
            if (loanBooks.some(lb => lb.type === 'entity')) {
                // PR #1510: office-scope the entity books FIRST — this is an OFFICE
                // page, so a multi-office unified tenant (Entourage post-flip) shows
                // only this office's books (Geelong → its 3, Cremorne → its own).
                // filterEntityBooksForOffice falls back to the unfiltered list when
                // no book matches / books lack officeId, so single-office unified
                // tenants (Timms/Decru/VFA) are byte-identical. Grouping and the
                // 'entity:<code>' tab-id encoding below are unchanged.
                const officeScopedBooks = filterEntityBooksForOffice(loanBooks, office?.id);
                const entityGroups = new Map<string, { name: string; loanBooks: typeof loanBooks }>();
                const standaloneBooks: typeof loanBooks = [];
                for (const lb of officeScopedBooks) {
                    if (lb.type === 'entity' && lb.entityCode) {
                        const existing = entityGroups.get(lb.entityCode);
                        if (existing) {
                            existing.loanBooks.push(lb);
                        } else {
                            // Display name: strip parenthetical "(Choice)" / "(LMG)" suffixes so
                            // grouped entity tabs read cleanly. Fallback to first loan_book.name.
                            const displayName = (lb.name || '').replace(/\s*\([^)]*\)\s*$/, '').trim() || lb.name;
                            entityGroups.set(lb.entityCode, { name: displayName, loanBooks: [lb] });
                        }
                    } else {
                        // Entity-type loan_book without entityCode falls back to per-loan_book tab.
                        standaloneBooks.push(lb);
                    }
                }
                const entityTabs = [...entityGroups.entries()].map(([entityCode, { name }]) => ({
                    id: `entity:${entityCode}`,
                    name,
                }));
                const standaloneTabs = standaloneBooks.map(lb => ({ id: lb.id, name: lb.name }));
                return [...entityTabs, ...standaloneTabs];
            }
            // Template-type loan books (Entourage): filter by templates with data in this office
            if (templateIds.length > 0) {
                const filtered = loanBooks.filter(lb => templateIds.includes(lb.templateId));
                if (filtered.length > 0) {
                    return filtered.map(lb => ({ id: lb.id, name: lb.name }));
                }
            }
            return loanBooks.map(lb => ({ id: lb.id, name: lb.name }));
        }
        return filteredFileTemplates;
    }, [loanBooks, filteredFileTemplates, templateIds, office?.id]);

    // Calculate monthly chart data for commissions (uses filtered aggregates)
    const monthlyChartData = useMemo(() => {
        const filterYear = parseInt(String(filters.year), 10);
        const monthsTemplate = Array.from({ length: 12 }, (_, i) => {
            const monthIndex = filters.yearType === 'financial' ? (i + 6) % 12 : i;
            const yearForMonth = filters.yearType === 'financial' && monthIndex < 6 ? filterYear + 1 : filterYear;
            return { year: yearForMonth, month: monthIndex + 1 };
        });

        return monthsTemplate.map(({ year, month }) => {
            const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
            const monthData = filteredAggregates.find(agg => agg.yearMonth === yearMonth);

            return {
                month: new Date(year, month - 1).toLocaleString('default', { month: 'short' }),
                // ✅ CRITICAL: Convert cents to dollars for Recharts display
                // Backend stores as integer cents (e.g., 123456), charts need dollars (1234.56)
                upfront: monthData?.upfrontCommission ? monthData.upfrontCommission / 100 : 0,
                trail: monthData?.trailCommission ? monthData.trailCommission / 100 : 0,
            };
        });
    }, [filteredAggregates, filters.year, filters.yearType]);

    // ✅ FIXED: Calculate settlement chart data using ACTUAL settlement data
    // ✅ BROKER FILTERING: Apply broker filtering to settlement data using brokerDisplay
    // ✅ ACTIVITY STATUS FILTERING: Filter settlements by active/inactive status
    // ✅ LENDER FILTERING: Filter settlements by lender (orthogonal to broker/office dimensions)
    // NOTE: Filters are exclusive (if/else) because byBroker and byLender are separate orthogonal
    // dimensions. Priority: 1. Broker (most specific), 2. Activity status, 3. Lender
    // Combined filtering (e.g., "Broker A from Lender X") would require backend support.
    const settlementChartData = useMemo(() => {
        const filterYear = parseInt(String(filters.year), 10);
        const monthsTemplate = Array.from({ length: 12 }, (_, i) => {
            const monthIndex = filters.yearType === 'financial' ? (i + 6) % 12 : i;
            const yearForMonth = filters.yearType === 'financial' && monthIndex < 6 ? filterYear + 1 : filterYear;
            return { year: yearForMonth, month: monthIndex + 1 };
        });

        return monthsTemplate.map(({ year, month }) => {
            const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
            const settlementData = settlements.find(s => s.yearMonth === yearMonth);

            let totalSettled = settlementData?.totalSettled || 0;
            let count = settlementData?.settlementCount || 0;

            // ✅ Apply individual broker filtering if broker is selected
            if (filters.broker !== 'all' && settlementData?.byBroker) {
                // Find broker by display name
                const brokerEntry = Object.entries(settlementData.byBroker).find(
                    ([_, data]: [string, any]) => data.brokerDisplay === filters.broker
                );

                if (brokerEntry) {
                    const [_, brokerData] = brokerEntry as [string, any];
                    totalSettled = brokerData.total || 0;
                    count = brokerData.count || 0;
                } else {
                    // Broker has no settlements this month
                    totalSettled = 0;
                    count = 0;
                }
            }
            // ✅ Apply activity status filtering if active/inactive is selected
            else if (filters.activityStatus !== 'all' && settlementData?.byBroker) {
                let filteredTotal = 0;
                let filteredCount = 0;

                Object.entries(settlementData.byBroker).forEach(([brokerId, brokerData]: [string, any]) => {
                    const brokerDisplay = brokerData.brokerDisplay || brokerId;
                    const status = brokerActivityStatus.get(brokerDisplay) || 'active';

                    // Only include brokers matching the selected activity status
                    if (status === filters.activityStatus) {
                        filteredTotal += brokerData.total || 0;
                        filteredCount += brokerData.count || 0;
                    }
                });

                totalSettled = filteredTotal;
                count = filteredCount;
            }
            // ✅ Apply lender filtering if lender is selected
            else if (filters.lender && filters.lender !== 'all') {
                // DEBUG: Log settlement byLender state
                if (settlementData) {
                    console.log(`[OfficeDashboard] Settlement ${yearMonth} byLender:`, {
                        hasData: !!settlementData.byLender,
                        lenders: settlementData.byLender ? Object.keys(settlementData.byLender).slice(0, 5) : 'none',
                        targetLender: filters.lender,
                    });
                }
                // ✅ Use global byLender (template-scoped lender filtering not yet implemented in backend)
                if (settlementData?.byLender) {
                    const lenderData = settlementData.byLender[filters.lender];
                    if (lenderData) {
                        totalSettled = lenderData.total || 0;
                        count = lenderData.count || 0;
                        console.log(`[OfficeDashboard] Settlement ${yearMonth} ANZ data:`, lenderData);
                    } else {
                        // No settlements for this lender this month
                        totalSettled = 0;
                        count = 0;
                    }
                } else {
                    // No lender data available
                    totalSettled = 0;
                    count = 0;
                }
            }

            // Convert cents to dollars for display
            const totalSettledDollars = totalSettled / 100;

            return {
                month: new Date(year, month - 1).toLocaleString('default', { month: 'short' }),
                yearMonth,  // Tooltip shows month + year
                totalSettled: totalSettledDollars,  // ✅ Actual settlement data in dollars (broker/lender-filtered)
                settledCents: totalSettled,
                settlementCount: count,
            };
        });
    }, [settlements, filters.year, filters.yearType, filters.broker, filters.activityStatus, filters.lender, brokerActivityStatus]);

    // "Loans Settled" tile: the selected period's settlements, by settlement
    // date, each loan split counted once. Unfiltered, it is the backend's
    // distinct totals (the same figures as the Dashboard card and Commission
    // Breakdown footer). A single-broker or lender filter sums the filtered
    // months the chart shows: a loan settles in one month, has one lender, and
    // one broker's own loans never repeat, so that sum is still per loan.
    // The ACTIVITY filter sums the per-broker breakdown across many brokers,
    // which counts a split loan once per broker at its full amount, so the
    // tile shows no figure (null) rather than an inflated one.
    const settledTile = useMemo<{ count: number; cents: number } | null>(() => {
        if (filters.broker === 'all' && filters.activityStatus !== 'all') return null;
        const subFiltered = filters.broker !== 'all'
            || (!!filters.lender && filters.lender !== 'all');
        if (!subFiltered) return { count: uniqueTotalCount, cents: uniqueTotalSettled };
        return settlementChartData.reduce(
            (acc, m) => ({ count: acc.count + m.settlementCount, cents: acc.cents + m.settledCents }),
            { count: 0, cents: 0 },
        );
    }, [filters.broker, filters.activityStatus, filters.lender, uniqueTotalCount, uniqueTotalSettled, settlementChartData]);

    // ✅ BROKER FILTERING: Calculate line of trail growth chart data from filtered aggregates
    // ✅ FIXED (2025-11-16): Use lineOfTrailSeries from useLineOfTrailKpi hook
    // This correctly queries upload_runs table with composite key logic
    // Previously used filteredAggregates which had no line of trail data (all 0s)

    // ✅ FIX #2-5: Filter LOT series by broker/activity status/lender when applicable
    // FIX (2025-12-12): When byBroker is unavailable but filtering is required, return zeros (not unfiltered data)
    // FIX (2025-12-13): Added byLender support for lender filtering
    const filteredLineOfTrailSeries = useMemo(() => {
        // No filtering needed if all brokers selected with no activity filter and no lender filter
        if (filters.activityStatus === 'all' && filters.broker === 'all' && (!filters.lender || filters.lender === 'all')) {
            return lineOfTrailSeries;
        }

        // ✅ FIX (2025-12-13): Use byLender for lender filtering
        if (filters.lender && filters.lender !== 'all') {
            return lineOfTrailSeries.map(item => {
                if (!item.byLender) {
                    // No byLender data available - return zeros
                    return { ...item, lot: 0, loanAmount: 0 };
                }

                const lenderData = item.byLender[filters.lender!];
                if (lenderData) {
                    return { ...item, lot: lenderData.lot, loanAmount: lenderData.loanAmount };
                }

                // Lender has no data for this month
                return { ...item, lot: 0, loanAmount: 0 };
            });
        }

        return lineOfTrailSeries.map(item => {
            // FIX (2025-12-12): If no broker breakdown available but filtering is required, return zeros
            // This prevents showing unfiltered data when user expects filtered results
            if (!item.byBroker) return { ...item, lot: 0, loanAmount: 0 };

            let filteredLot = 0;
            let filteredLoanAmount = 0;

            Object.entries(item.byBroker).forEach(([brokerDisplay, data]) => {
                // Individual broker filter
                if (filters.broker !== 'all' && brokerDisplay !== filters.broker) return;

                // Activity status filter
                if (filters.activityStatus !== 'all') {
                    const status = brokerActivityStatus.get(brokerDisplay) || 'active';
                    if (status !== filters.activityStatus) return;
                }

                filteredLot += data.lot;
                filteredLoanAmount += data.loanAmount;
            });

            return { ...item, lot: filteredLot, loanAmount: filteredLoanAmount };
        });
    }, [lineOfTrailSeries, filters.broker, filters.activityStatus, filters.lender, brokerActivityStatus]);

    // Loan Book chart data from monthly_lot_snapshots (deduplicated via active_loans_snapshot)
    // FIX: Previous source (upload_runs) double/triple counted loans across offices and runs
    // filteredLineOfTrailSeries already handles broker/activity/lender filtering for loanAmount
    const loanBookChartData = useMemo(() => {
        return filteredLineOfTrailSeries.map(item => ({
            month: item.month,
            loanBook: (item.loanAmount || 0) / 100,
        }));
    }, [filteredLineOfTrailSeries]);

    // ✅ FIX (2025-12-12): Calculate filtered commission totals for stat cards
    // The hook returns unfiltered totals; we need filtered totals for broker/activity/lender filtering
    const filteredUpfrontCommission = useMemo(() =>
        filteredAggregates.reduce((sum, agg) => sum + (agg.upfrontCommission || 0), 0),
        [filteredAggregates]
    );
    const filteredTrailCommission = useMemo(() =>
        filteredAggregates.reduce((sum, agg) => sum + (agg.trailCommission || 0), 0),
        [filteredAggregates]
    );

    // ✅ FIX (2025-12-12): Calculate filtered previous year commission totals for YoY comparison
    // We need to apply the same filtering logic to previous year aggregates
    const filteredPreviousAggregates = useMemo(() => {
        let baseFiltered = previousAggregates;

        // Apply enhanced filters first (if enabled)
        if (featureFlags.enhancedFilters) {
            baseFiltered = filterAggregates(previousAggregates, filters);
        }

        // Apply activity status filter
        if (filters.activityStatus !== 'all') {
            baseFiltered = baseFiltered.map(agg => {
                if (!agg.byBroker) return agg;
                let totalUpfront = 0, totalTrail = 0;
                Object.entries(agg.byBroker).forEach(([brokerId, data]) => {
                    const brokerDisplay = data.brokerDisplay || brokerId;
                    const status = brokerActivityStatus.get(brokerDisplay) || 'active';
                    if (status === filters.activityStatus) {
                        totalUpfront += data.upfrontNet || 0;
                        totalTrail += data.trailNet || 0;
                    }
                });
                return { ...agg, upfrontCommission: totalUpfront, trailCommission: totalTrail };
            });
        }

        // Apply lender filtering
        if (filters.lender && filters.lender !== 'all') {
            baseFiltered = baseFiltered.map(agg => {
                if (!agg.byLender) return { ...agg, upfrontCommission: 0, trailCommission: 0 };
                const lenderData = agg.byLender[filters.lender!];
                if (lenderData) {
                    return { ...agg, upfrontCommission: lenderData.upfrontNet || 0, trailCommission: lenderData.trailNet || 0 };
                }
                return { ...agg, upfrontCommission: 0, trailCommission: 0 };
            });
        }

        // Apply broker filtering
        if (filters.broker !== 'all') {
            baseFiltered = baseFiltered.map(agg => {
                const brokerData = agg.byBroker?.[Object.keys(agg.byBroker || {}).find(
                    key => agg.byBroker![key].brokerDisplay === filters.broker
                ) || ''];
                if (brokerData) {
                    return { ...agg, upfrontCommission: brokerData.upfrontNet || 0, trailCommission: brokerData.trailNet || 0 };
                }
                return { ...agg, upfrontCommission: 0, trailCommission: 0 };
            });
        }

        return baseFiltered;
    }, [previousAggregates, filters, brokerActivityStatus]);

    const filteredPreviousUpfrontCommission = useMemo(() =>
        filteredPreviousAggregates.reduce((sum, agg) => sum + (agg.upfrontCommission || 0), 0),
        [filteredPreviousAggregates]
    );
    const filteredPreviousTrailCommission = useMemo(() =>
        filteredPreviousAggregates.reduce((sum, agg) => sum + (agg.trailCommission || 0), 0),
        [filteredPreviousAggregates]
    );

    // ✅ NEW: Calculate YoY growth percentages (using filtered values)
    const yoyGrowth = useYoYComparison(
        {
            upfront: filteredUpfrontCommission,
            trail: filteredTrailCommission,
            loanBook: totalLoanBook,
            lineOfTrail: lineOfTrail,
        },
        {
            upfront: filteredPreviousUpfrontCommission,
            trail: filteredPreviousTrailCommission,
            loanBook: previousLoanBook,
            lineOfTrail: previousLineOfTrail,
        }
    );

    // Calculate broker revenue percentages across all months (office-scoped, filtered by activity status)
    const brokerRevenues = useMemo(() => {
        if (brokerBreakdownStatus === 'all') {
            return calculateBrokerPercentagesAcrossMonths(filteredAggregates);
        }
        const statusFiltered = filterAggregatesByBrokerStatus(
            filteredAggregates,
            brokerActivityStatus,
            brokerBreakdownStatus
        );
        return calculateBrokerPercentagesAcrossMonths(statusFiltered);
    }, [filteredAggregates, brokerBreakdownStatus, brokerActivityStatus]);

    // Check for concentration risk
    const concentrationRisk = useMemo(() => {
        return getConcentrationRisk(brokerRevenues, 25);
    }, [brokerRevenues]);

    // ✅ PHASE 1B: Granular loading states for progressive rendering
    const isInitialLoading = loadingAggregates && loadingLoanBook && loadingHistorical && loadingSettlements && loadingPreviousAggregates;

    // ✅ PHASE 1B: Validate context data exists (after all hooks are called)
    if (!context || !userData || !companyData) {
        console.error('[OfficeDashboard] Missing context data');
        return (
            <div className="p-8 text-center text-white">
                <p className="text-red-400">Failed to load user context. Please refresh the page.</p>
            </div>
        );
    }

    // ✅ PHASE 1B: Validate office exists and user has access
    if (!office) {
        return (
            <div className="p-8 text-center">
                <div className="bg-yellow-900/20 border border-yellow-500 rounded-lg p-6 max-w-md mx-auto">
                    <h3 className="text-yellow-400 font-semibold mb-2">Office Not Found</h3>
                    <p className="text-gray-300">
                        The requested office does not exist or you don't have permission to view it.
                    </p>
                </div>
            </div>
        );
    }

    // ✅ PHASE 1B: Show full skeleton only on initial load
    if (isInitialLoading) {
        return <SkeletonDashboard showTemplateTabs={loanBookTabs.length > 1} />;
    }

    return (
        <ErrorBoundary context="OfficeDashboard">
            <div className="space-y-6">
                <PageHeader
                    icon={Building2}
                    title={`${office.name} Dashboard`}
                    subtitle="Office-specific commission overview"
                />

                {/* Concentration Risk Alert — hidden for brokers (always 100% of own data) */}
                {concentrationRisk && userData?.role !== 'broker' && userData?.role !== 'broker_assistant' && (
                    <ConcentrationAlert broker={concentrationRisk} threshold={25} />
                )}

                {/* Who To Call — office-scoped trail drop-off alerts, hidden for brokers */}
                {userData?.companyId && userData?.role !== 'broker' && userData?.role !== 'broker_assistant' && (
                    <WhoToCallWidget
                        companyId={userData.companyId}
                        officeId={office.id}
                        hideCommissions={hideCommissionsClaim}
                    />
                )}

                {/* Loan Book Tabs - Show if 2+ loan books available */}
                {loanBookTabs.length > 1 && (
                <div className="mb-6 border-b border-gray-700">
                    <nav className="flex space-x-6 overflow-x-auto">
                        <button
                            onClick={() => setSelectedTemplate('all')}
                            className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors whitespace-nowrap ${
                                selectedTemplate === 'all'
                                    ? 'text-blue-500 border-blue-500'
                                    : 'text-gray-400 border-transparent hover:text-white hover:border-gray-500'
                            }`}
                        >
                            All Loan Books
                        </button>
                        {loanBookTabs.map((tab: any) => (
                            <button
                                key={tab.id}
                                onClick={() => setSelectedTemplate(tab.id)}
                                className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors whitespace-nowrap ${
                                    selectedTemplate === tab.id
                                        ? 'text-blue-500 border-blue-500'
                                        : 'text-gray-400 border-transparent hover:text-white hover:border-gray-500'
                                }`}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </nav>
                </div>
            )}
            
            {/* Filters - ✅ PHASE 3: Use enhanced filters when feature flag enabled */}
            <ErrorBoundary
                context="DashboardFilters"
                fallback={
                    <div className="bg-gray-800 p-4 rounded-lg mb-6">
                        <p className="text-yellow-400 text-sm">Filters temporarily unavailable</p>
                    </div>
                }
            >
                {featureFlags.enhancedFilters ? (
                    <EnhancedDashboardFilters
                        brokers={filteredBrokers}
                        lenders={lenders}
                        filters={filters}
                        setFilters={setFilters}
                        userRole={userData?.role}
                        isOfficeDashboard={true}
                        hideCommissionType={true}
                        hidePerformance={true}
                        earliestYear={earliestYear}
                        latestYear={latestYear}
                    />
                ) : (
                    <DashboardFilters
                        brokers={filteredBrokers}
                        filters={filters}
                        setFilters={setFilters}
                        userRole={userData?.role}
                        isOfficeDashboard={true}
                        earliestYear={earliestYear}
                        latestYear={latestYear}
                    />
                )}
            </ErrorBoundary>

            {/* Stat Cards with progressive loading */}
            <ErrorBoundary
                context="StatCards"
                fallback={
                    <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 mt-6">
                        <p className="text-red-400">Failed to load statistics</p>
                    </div>
                }
            >
                {loadingAggregates || loadingLoanBook || loadingSettlements ? (
                    <SkeletonStatCards count={5} columns={3} />
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-6 mt-6">
                        <StatCardWithGrowth
                            title="Upfront Commission"
                            subtitle={`${filters.yearType === 'financial'
                                ? `FY ${filters.year}/${filters.year + 1}`
                                : `Year ${filters.year}`
                            } • ${COMMISSION_GST_BASIS}`}
                            value={hideCommissions ? '\u2014' : formatCurrencyShorthand(filteredUpfrontCommission)}
                            icon={<HandCoins size={24} />}
                            gradientColor="#10b981"
                            isUpdating={isAggregating}
                            growthPercentage={hideCommissions ? undefined : yoyGrowth.upfront}
                            showGrowth={!hideCommissions}
                        />
                        <StatCardWithGrowth
                            title="Trail Commission"
                            subtitle={`${filters.yearType === 'financial'
                                ? `FY ${filters.year}/${filters.year + 1}`
                                : `Year ${filters.year}`
                            } • ${COMMISSION_GST_BASIS}`}
                            value={hideCommissions ? '\u2014' : formatCurrencyShorthand(filteredTrailCommission)}
                            icon={<TrendingUp size={24} />}
                            gradientColor="#3b82f6"
                            isUpdating={isAggregating}
                            growthPercentage={hideCommissions ? undefined : yoyGrowth.trail}
                            showGrowth={!hideCommissions}
                        />
                        <StatCardWithGrowth
                            title="Loan Book"
                            subtitle={filters.yearType === 'financial'
                                ? `FY ${filters.year}/${filters.year + 1}`
                                : `Year ${filters.year}`
                            }
                            value={hideLoanVolume ? '\u2014' : formatLoanBook(totalLoanBook)}
                            icon={<Vault size={24} />}
                            gradientColor="#f59e0b"
                            isUpdating={isAggregating}
                            growthPercentage={hideLoanVolume ? undefined : yoyGrowth.loanBook}
                            showGrowth={!hideLoanVolume}
                        />
                        <StatCardWithGrowth
                            title="Loans Settled"
                            subtitle={`${filters.yearType === 'financial'
                                ? `FY ${filters.year}/${filters.year + 1}`
                                : `Year ${filters.year}`
                            } • ${settledTile
                                ? `${settledTile.count.toLocaleString('en-AU')} ${settledTile.count === 1 ? 'loan' : 'loans'}`
                                : '\u2014 loans'}`}
                            value={hideLoanVolume || !settledTile ? '\u2014' : formatCurrencyShorthand(settledTile.cents)}
                            icon={<Handshake size={24} />}
                            gradientColor="#06b6d4"
                            isUpdating={isAggregating}
                            showGrowth={false}
                            helpTooltip={settledTile ? undefined : {
                                definition: 'Totals per loan are not available with the active/inactive broker filter: a loan split between brokers would be counted once per broker. Clear the filter, or pick a single broker.',
                            }}
                        />
                        <StatCardWithGrowth
                            title="Line of Trail"
                            subtitle={`${filters.yearType === 'financial'
                                ? `FY ${filters.year}/${filters.year + 1}`
                                : `Year ${filters.year}`
                            } • ${lineOfTrail.toLocaleString()} active loans`}
                            value={lineOfTrail.toLocaleString()}
                            icon={<Activity size={24} />}
                            gradientColor="#8b5cf6"
                            isUpdating={isAggregating}
                            growthPercentage={yoyGrowth.lineOfTrail}
                            showGrowth={true}
                        />
                    </div>
                )}
            </ErrorBoundary>
            
            {/* Charts with progressive loading - EXACT MATCH to Main Dashboard */}
            <ErrorBoundary
                context="DashboardCharts"
                fallback={
                    <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 mt-6">
                        <p className="text-red-400">Failed to load charts</p>
                    </div>
                }
            >
                {loadingHistorical || loadingSettlements ? (
                    <SkeletonCharts />
                ) : (
                    <div className="grid grid-cols-1 gap-6 mt-6">
                        {/* Upfront Commission with empty state detection */}
                        {hideCommissions ? (
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg relative overflow-hidden">
                                <h3 className="font-bold text-lg mb-4 text-white">Upfront Commission</h3>
                                <div className="p-8 bg-gray-700 rounded text-center">
                                    <p className="text-gray-400">Commission data is hidden for your account.</p>
                                </div>
                            </div>
                        ) : monthlyChartData.length > 0 && monthlyChartData.some(item => item.upfront > 0) ? (
                            <MonthlyBreakdownChart
                                data={monthlyChartData}
                                title="Upfront Commission"
                                dataKey="upfront"
                                color="#10b981"
                            />
                        ) : (
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg mt-6 relative overflow-hidden">
                                <div className="absolute inset-0 opacity-10"
                                     style={{ background: 'radial-gradient(circle at 50% 0%, #10b98140 0%, transparent 70%)' }}
                                />
                                <h3 className="font-bold text-lg mb-4 text-white relative z-10">Upfront Commission</h3>
                                <div className="p-8 bg-gray-700 rounded text-center relative z-10">
                                    <p className="text-gray-400">
                                        {loadingAggregates
                                            ? 'Loading upfront commission data...'
                                            : selectedTemplate && selectedTemplate !== 'all'
                                                ? `No upfront commission data for the selected template in ${filters.yearType === 'financial' ? 'FY' : ''} ${filters.year}.`
                                                : `No upfront commission data for ${office.name} in ${filters.yearType === 'financial' ? 'FY' : ''} ${filters.year}.`
                                        }
                                    </p>
                                    {!loadingAggregates && (
                                        <p className="text-gray-500 text-sm mt-2">
                                            Try selecting "All Templates" or a different year/period.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Trail Commission with empty state detection */}
                        {hideCommissions ? (
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg relative overflow-hidden">
                                <h3 className="font-bold text-lg mb-4 text-white">Trail Commission</h3>
                                <div className="p-8 bg-gray-700 rounded text-center">
                                    <p className="text-gray-400">Commission data is hidden for your account.</p>
                                </div>
                            </div>
                        ) : monthlyChartData.length > 0 && monthlyChartData.some(item => item.trail > 0) ? (
                            <MonthlyBreakdownChart
                                data={monthlyChartData}
                                title="Trail Commission"
                                dataKey="trail"
                                color="#3b82f6"
                            />
                        ) : (
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg mt-6 relative overflow-hidden">
                                <div className="absolute inset-0 opacity-10"
                                     style={{ background: 'radial-gradient(circle at 50% 0%, #3b82f640 0%, transparent 70%)' }}
                                />
                                <h3 className="font-bold text-lg mb-4 text-white relative z-10">Trail Commission</h3>
                                <div className="p-8 bg-gray-700 rounded text-center relative z-10">
                                    <p className="text-gray-400">
                                        {loadingAggregates
                                            ? 'Loading trail commission data...'
                                            : selectedTemplate && selectedTemplate !== 'all'
                                                ? `No trail commission data for the selected template in ${filters.yearType === 'financial' ? 'FY' : ''} ${filters.year}.`
                                                : `No trail commission data for ${office.name} in ${filters.yearType === 'financial' ? 'FY' : ''} ${filters.year}.`
                                        }
                                    </p>
                                    {!loadingAggregates && (
                                        <p className="text-gray-500 text-sm mt-2">
                                            Try selecting "All Templates" or a different year/period.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Loan Book with empty state detection (CHART #3) - loan VOLUME,
                            so it survives the portfolio totals switch. */}
                        {hideLoanVolume ? (
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg relative overflow-hidden">
                                <h3 className="font-bold text-lg mb-4 text-white">Loan Book</h3>
                                <div className="p-8 bg-gray-700 rounded text-center">
                                    <p className="text-gray-400">Commission data is hidden for your account.</p>
                                </div>
                            </div>
                        ) : loanBookChartData.length > 0 && loanBookChartData.some(item => item.loanBook > 0) ? (
                            <MonthlyBreakdownChart
                                data={loanBookChartData}
                                title="Loan Book"
                                dataKey="loanBook"
                                color="#f59e0b"
                            />
                        ) : (
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg mt-6 relative overflow-hidden">
                                <div className="absolute inset-0 opacity-10"
                                     style={{ background: 'radial-gradient(circle at 50% 0%, #f59e0b40 0%, transparent 70%)' }}
                                />
                                <h3 className="font-bold text-lg mb-4 text-white relative z-10">Loan Book</h3>
                                <div className="p-8 bg-gray-700 rounded text-center relative z-10">
                                    <p className="text-gray-400">
                                        {loadingHistorical
                                            ? 'Loading loan book data...'
                                            : selectedTemplate && selectedTemplate !== 'all'
                                                ? `No loan book data for the selected template in ${filters.yearType === 'financial' ? 'FY' : ''} ${filters.year}.`
                                                : `No loan book data for ${office.name} in ${filters.yearType === 'financial' ? 'FY' : ''} ${filters.year}.`
                                        }
                                    </p>
                                    {!loadingHistorical && (
                                        <p className="text-gray-500 text-sm mt-2">
                                            Try selecting "All Templates" or a different year/period.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Line of Trail Growth with empty state detection (CHART #4) */}
                        {/* ✅ FIX: Use filteredLineOfTrailSeries for broker/activity filtering */}
                        {filteredLineOfTrailSeries.length > 0 && filteredLineOfTrailSeries.some(item => item.lot > 0) ? (
                            <MonthlyBreakdownChart
                                data={filteredLineOfTrailSeries}
                                title="Line of Trail Growth"
                                dataKey="lot"
                                color="#8b5cf6"
                                variant="count"
                            />
                        ) : (
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg mt-6 relative overflow-hidden">
                                <div className="absolute inset-0 opacity-10"
                                     style={{ background: 'radial-gradient(circle at 50% 0%, #8b5cf640 0%, transparent 70%)' }}
                                />
                                <h3 className="font-bold text-lg mb-4 text-white relative z-10">Line of Trail Growth</h3>
                                <div className="p-8 bg-gray-700 rounded text-center relative z-10">
                                    <p className="text-gray-400">
                                        {loadingHistorical
                                            ? 'Loading line of trail data...'
                                            : selectedTemplate && selectedTemplate !== 'all'
                                                ? `No line of trail data for the selected template in ${filters.yearType === 'financial' ? 'FY' : ''} ${filters.year}.`
                                                : `No line of trail data for ${office.name} in ${filters.yearType === 'financial' ? 'FY' : ''} ${filters.year}.`
                                        }
                                    </p>
                                    {!loadingHistorical && (
                                        <p className="text-gray-500 text-sm mt-2">
                                            Try selecting "All Templates" or a different year/period.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Total Loans Settled with empty state detection (CHART #5) - loan
                            VOLUME, so it survives the portfolio totals switch. */}
                        {hideLoanVolume ? (
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg relative overflow-hidden">
                                <h3 className="font-bold text-lg mb-4 text-white">Total Loans Settled</h3>
                                <div className="p-8 bg-gray-700 rounded text-center">
                                    <p className="text-gray-400">Commission data is hidden for your account.</p>
                                </div>
                            </div>
                        ) : settlementChartData.length > 0 && settlementChartData.some(item => item.totalSettled > 0) ? (
                            <MonthlyBreakdownChart
                                data={settlementChartData}
                                title="Total Loans Settled"
                                dataKey="totalSettled"
                                color="#06b6d4"
                                countKey="settlementCount"
                                countUnit={['loan', 'loans']}
                            />
                        ) : (
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg mt-6 relative overflow-hidden">
                                <div className="absolute inset-0 opacity-10"
                                     style={{ background: 'radial-gradient(circle at 50% 0%, #06b6d440 0%, transparent 70%)' }}
                                />
                                <h3 className="font-bold text-lg mb-4 text-white relative z-10">Total Loans Settled</h3>
                                <div className="p-8 bg-gray-700 rounded text-center relative z-10">
                                    <p className="text-gray-400">
                                        {loadingSettlements
                                            ? 'Loading settlement data...'
                                            : selectedTemplate && selectedTemplate !== 'all'
                                                ? `No settlement data for the selected template in ${filters.yearType === 'financial' ? 'FY' : ''} ${filters.year}.`
                                                : `No settlement data for ${office.name} in ${filters.yearType === 'financial' ? 'FY' : ''} ${filters.year}.`
                                        }
                                    </p>
                                    {!loadingSettlements && (
                                        <p className="text-gray-500 text-sm mt-2">
                                            Try selecting "All Templates" or a different year/period.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Top Lenders Analysis — hidden when commission visibility is restricted */}
                        {!hideCommissions && (
                            <TopLendersWidget
                                aggregates={filteredAggregates}
                                title="Top Lenders Analysis"
                                defaultLimit={5}
                                color="#ec4899"
                            />
                        )}
                    </div>
                )}
            </ErrorBoundary>

            {/* Broker Concentration Analysis — hidden for brokers and when commission visibility is restricted */}
            {!hideCommissions && userData?.role !== 'broker' && userData?.role !== 'broker_assistant' && (
                <>
                    <div className="flex items-center justify-between mt-6 mb-2">
                        <h3 className="text-lg font-bold text-white">Broker Concentration Analysis</h3>
                        <div className="flex gap-x-1 bg-gray-700 rounded-md p-1">
                            {(['active', 'all', 'inactive'] as const).map((status) => (
                                <button
                                    key={status}
                                    onClick={() => setBrokerBreakdownStatus(status)}
                                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                        brokerBreakdownStatus === status
                                            ? 'bg-blue-600 text-white'
                                            : 'text-gray-300 hover:bg-gray-600'
                                    }`}
                                >
                                    {status === 'active' ? 'Active' : status === 'all' ? 'All' : 'Non-Active'}
                                </button>
                            ))}
                        </div>
                    </div>
                    {brokerRevenues.length > 0 ? (
                        <>
                            <BrokerConcentrationWidget brokers={brokerRevenues} threshold={5} />
                            <BrokerRevenueTable
                                brokers={brokerRevenues}
                                concentrationThreshold={25}
                                showTotals={true}
                            />
                        </>
                    ) : (
                        <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                            <p className="text-gray-400 text-center">
                                No {brokerBreakdownStatus === 'active' ? 'active' : brokerBreakdownStatus === 'inactive' ? 'non-active' : ''} broker data for the selected period.
                            </p>
                        </div>
                    )}
                </>
            )}

            {/* No data message */}
            {!loadingAggregates && aggregates.length === 0 && (
                <div className="mt-8 p-4 bg-gray-800 rounded-lg text-center">
                    <p className="text-gray-400">
                        No commission data for the selected period.
                        Upload commission files for {office.name} to see analytics.
                    </p>
                </div>
            )}
            </div>
        </ErrorBoundary>
    );
};

export default OfficeDashboardPage;